import { Stack, useTheme } from "@mui/material";
import { useEffect, useRef } from "react";
import { ChatComposer } from "web_myplann/Chat/Components/Composer/ChatComposer";
import { ChatConversationActionsDrawer } from "web_myplann/Chat/Components/Conversation/ChatConversationActionsDrawer";
import { ChatEmptyState } from "web_myplann/Chat/Components/Conversation/ChatEmptyState";
import { ChatHeader } from "web_myplann/Chat/Components/Conversation/ChatHeader";
import { ChatSkeleton } from "web_myplann/Chat/Components/Conversation/ChatSkeleton";
import { ChatMessageList } from "web_myplann/Chat/Components/Messages/ChatMessageList";
import { useChatPage } from "web_myplann/Chat/Hooks/Conversation/useChatPage";
import { useChatScrollAnchor } from "web_myplann/Chat/Hooks/Conversation/useChatScrollAnchor";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { ConfirmDrawer } from "web_myplann/Shared/Components/ConfirmDrawer";
import { useConfirmableAction } from "web_myplann/Shared/Hooks/useConfirmableAction";

export const ChatPage = () => {
	const theme = useTheme();
	const { t } = useResource();
	const scrollRef = useRef<HTMLDivElement>(null);
	const hasAnchoredRef = useRef(false);
	const anchorToBottom = useChatScrollAnchor(scrollRef);
	const {
		isActionOpen,
		isConfirmOpen,
		openAction,
		closeAction,
		requestConfirm,
		closeConfirm,
	} = useConfirmableAction();
	const {
		title,
		titlePhoto,
		advisorName,
		advisorPhoto,
		canLeaveConversation,
		isLeavingConversation,
		messages,
		isLoading,
		hasOlder,
		isLoadingOlder,
		loadOlder,
		input,
		setInput,
		canAttach,
		onSend,
		onRetry,
		onPickPhoto,
		onPickGallery,
		onPickFile,
		onLeaveConversation,
		onBack,
	} = useChatPage({ onMessageSent: anchorToBottom });

	useEffect(() => {
		if (hasAnchoredRef.current || messages.length === 0) return;
		hasAnchoredRef.current = true;
		anchorToBottom();
	}, [messages.length, anchorToBottom]);

	const hasMessages = messages.length > 0;

	return (
		<Stack height="100dvh" bgcolor={theme.palette.background.paper}>
			<ChatHeader
				name={title}
				photo={titlePhoto}
				onBack={onBack}
				onOpenActions={
					canLeaveConversation && !isLeavingConversation
						? openAction
						: undefined
				}
			/>

			<Stack
				ref={scrollRef}
				flex={1}
				minHeight={0}
				px={theme.spacingTokens.md}
				py={theme.spacingTokens.md}
				bgcolor={theme.palette.surface.muted}
				sx={{ overflowY: "auto" }}
			>
				{isLoading ? (
					<ChatSkeleton />
				) : hasMessages ? (
					<ChatMessageList
						messages={messages}
						advisorName={advisorName}
						advisorPhoto={advisorPhoto}
						hasOlder={hasOlder}
						isLoadingOlder={isLoadingOlder}
						onLoadOlder={loadOlder}
						onRetry={onRetry}
					/>
				) : (
					<ChatEmptyState name={advisorName} />
				)}
			</Stack>

			<ChatComposer
				value={input}
				onChange={setInput}
				onSend={onSend}
				isFirstMessage={!hasMessages}
				canAttach={canAttach}
				onPickPhoto={onPickPhoto}
				onPickGallery={onPickGallery}
				onPickFile={onPickFile}
			/>

			<ChatConversationActionsDrawer
				open={isActionOpen}
				title={title}
				onClose={closeAction}
				onRequestLeave={requestConfirm}
			/>

			<ConfirmDrawer
				open={isConfirmOpen}
				title={t(Resources.Chat.Leave.confirmTitle)}
				description={t(Resources.Chat.Leave.confirmDescription)}
				confirmLabel={t(Resources.Chat.Leave.confirm)}
				cancelLabel={t(Resources.Chat.Leave.cancel)}
				destructive
				isPending={isLeavingConversation}
				onClose={closeConfirm}
				onConfirm={onLeaveConversation}
			/>
		</Stack>
	);
};
